import {
  isSidebarSectionOpen,
  readCollapsedSidebarSections,
  setCollapsedSidebarSections,
} from "@frontend-shared/utils";
import { useCallback, useMemo, useState } from "react";

import { localStorageAdapter } from "@/lib/storage-adapter";

interface IUseSidebarSectionCollapseProps {
  sectionKey: string;
  // NOTE: optional and last — only the blocks that ship closed (the archive)
  // pass it. Until the user touches such a block it stays closed, exactly as
  // before this became persistent.
  isCollapsedByDefault?: boolean;
}

interface IUseSidebarSectionCollapseResult {
  // NOTE: shaped for a Radix `type="multiple"` accordion holding a single item,
  // which is how these one-block sections are already built.
  openSectionKeys: string[];
  handleOpenSectionsChange: (nextOpenKeys: string[]) => void;
}

// NOTE: NSAI-5926 — the single-block counterpart of
// useCollapsedSidebarSections, for the sidebar's one-item accordions (Folders,
// Archived). Same persisted record, same storage entry — the two hooks only
// differ in how many keys they own, and each rewrites just its own.
export const useSidebarSectionCollapse = ({
  sectionKey,
  isCollapsedByDefault = false,
}: IUseSidebarSectionCollapseProps): IUseSidebarSectionCollapseResult => {
  const [isOpen, setIsOpen] = useState<boolean>(() =>
    isSidebarSectionOpen({
      collapsedSections: readCollapsedSidebarSections({
        storage: localStorageAdapter,
      }),
      sectionKey,
      isCollapsedByDefault,
    }),
  );

  const openSectionKeys = useMemo(
    () => (isOpen ? [sectionKey] : []),
    [isOpen, sectionKey],
  );

  const handleOpenSectionsChange = useCallback(
    (nextOpenKeys: string[]): void => {
      const isNextOpen = nextOpenKeys.includes(sectionKey);

      setCollapsedSidebarSections({
        storage: localStorageAdapter,
        sectionKeys: [sectionKey],
        collapsedSectionKeys: isNextOpen ? [] : [sectionKey],
      });
      setIsOpen(isNextOpen);
    },
    [sectionKey],
  );

  return { openSectionKeys, handleOpenSectionsChange };
};
